import apiClient from '../apiClient'
import { useAuthStore } from '@/stores/auth.store'
import { useTranslationStore } from '@/stores/translation.store'
import { putMessages } from '@/utils/db/messages'
import { trackEvent } from '@/utils/tracking'
import { trackAdCashEvent } from '@/utils/adcash'

const getTargetLanguage = () => {
    const translationStore = useTranslationStore()
    const authStore = useAuthStore()
    return (
        translationStore.getSelectedLanguage() ||
        authStore.user?.data?.preferredLanguage
    )
}

/**
 * Get messages for a specific conversation with pagination support
 * @param {string} conversationId - The conversation ID
 * @param {number} limit - Number of messages to fetch (default: 30)
 * @param {string|null} beforeTimestamp - Fetch messages before this timestamp (for pagination)
 * @param {boolean} markAsRead - Whether backend should mark fetched messages as read
 * @returns {Promise<Object>} - Object containing { messages: Array, hasMoreMessages: boolean }
 */
const getMessagesFromConversation = async (
    conversationId,
    limit,
    beforeTimestamp = null,
    markAsRead = false
) => {
    try {
        const targetLanguage = getTargetLanguage()

        const payload = {
            conversationId,
            targetLanguage,
            limit,
            // Only include beforeTimestamp if it's provided (for pagination)
            ...(beforeTimestamp && { beforeTimestamp }),
            markAsRead,
        }

        const response = await apiClient.post(
            '/messages/conversation/',
            payload
        )
        return response.data
    } catch (error) {
        console.error('Failed to retrieve messages via API:', error)
        throw new Error('Failed to retrieve messages: ' + error.message)
    }
}

/**
 * Get initial conversations with last message for each conversation
 * @returns {Promise<Object>} - Object containing { conversations: Array, messages: Array }
 */
const getInitialConversationsWithLastMessages = async () => {
    try {
        const targetLanguage = getTargetLanguage()

        const payload = {
            targetLanguage,
            markAsReceived: true,
        }

        const response = await apiClient.post(
            '/messages/conversations/preview',
            payload
        )
        return response.data
    } catch (error) {
        console.error(
            'Failed to retrieve conversation previews via API:',
            error
        )
        throw new Error(
            'Failed to retrieve conversation previews: ' + error.message
        )
    }
}

const getNewMessages = async (lastMessageDate) => {
    try {
        const targetLanguage = getTargetLanguage()

        const body = {
            lastMessageDate,
            targetLanguage,
            markAsReceived: true,
        }

        const response = await apiClient.post('/messages/user/new', body)
        return response.data || []
    } catch (error) {
        console.log('error', error)
        throw new Error('Failed to retrieve messages: ' + error.message)
    }
}

/**
 * Mark messages as received
 * @param {Array} messageIds - Array of message IDs to mark as received
 */
const markMessagesAsReceived = async (messageIds) => {
    try {
        await apiClient.post('/messages/received', {
            messageIds,
        })
        return true
    } catch (error) {
        console.error('Failed to mark messages as received:', error)
        return false
    }
}

/**
 * Mark messages as read
 * @param {Array} messageIds - Array of message IDs to mark as read
 */
const markMessagesAsRead = async (messageIds) => {
    try {
        await apiClient.post('/messages/read', {
            messagesIds: messageIds, // Note: API uses "messagesIds" instead of "messageIds"
        })
        return true
    } catch (error) {
        console.error('Failed to mark messages as read:', error)
        return false
    }
}

const sendMessage = async (newMessage, trackingContext = {}) => {
    try {
        const response = await apiClient.post('/messages/send', newMessage)
        const sent = response?.data?.message || response?.data || {}

        // Track in Mixpanel
        trackEvent('Message Sent', {
            messageId: sent._id,
            conversationId: sent.conversationId || newMessage.conversationId,
            sender: sent.sender || newMessage.sender,
            receiver: sent.receiver || newMessage.receiver,
            contentType: sent.contentType || newMessage.contentType,
            premium:
                typeof sent.premium !== 'undefined'
                    ? sent.premium
                    : !!newMessage.premium,
            ...trackingContext,
        })

        // Track in AdCash
        if (trackingContext?.isFirstMessage) {
            trackAdCashEvent(
                localStorage.getItem('adcashClickId'),
                'Activation',
                'c06896'
            )
        }

        return response.data
    } catch (error) {
        // Preserve the original Axios error so callers can inspect
        // error.response.status + error.response.data (e.g. 402 insufficient_message_credits)
        throw error
    }
}

const typing = async (conversationId) => {
    try {
        const response = await apiClient.post('/messages/typing', {
            conversationId,
        })
        return response.data
    } catch (error) {
        throw new Error('Failed to send typing notification: ' + error.message)
    }
}

const stopTyping = async (conversationId) => {
    try {
        const response = await apiClient.post('/messages/stoptyping', {
            conversationId,
        })
        return response.data
    } catch (error) {
        throw new Error(
            'Failed to send stop typing notification: ' + error.message
        )
    }
}

/**
 * Fetch translations for a batch of existing messages.
 * @param {string[]} messageIds - Array of message IDs to get translations for.
 * @param {string} targetLanguage - The language code to translate to.
 * @returns {Promise<{messages: Array}>} - Promise resolving to an object containing the updated messages.
 */
const fetchTranslationsForMessages = async (messageIds, targetLanguage) => {
    try {
        if (!messageIds || messageIds.length === 0) {
            return { messages: [] } // Don't call API if no IDs
        }
        const payload = {
            messageIds,
            targetLanguage,
        }
        const response = await apiClient.post(
            '/messages/translate-batch',
            payload
        )
        // Add messages to IndexedDB (async, fire-and-forget is okay here)
        if (response.data?.messages?.length > 0) {
            putMessages(response.data.messages).catch((err) => {
                console.error('DB Error saving fetched translations:', err)
            })
        }
        return response.data // Should be { messages: [...] }
    } catch (error) {
        console.error('Failed to fetch translations for messages:', error)
        throw new Error('Failed to fetch translations: ' + error.message)
    }
}

/**
 * Reload messages for a conversation with current language preferences
 * @param {string} conversationId - Conversation ID
 * @param {number} limit - Number of messages to fetch (default: 100)
 * @returns {Promise<Object>} - Object containing { messages: Array, hasMoreMessages: boolean }
 */
const reloadConversationMessages = async (conversationId, limit = 100) => {
    try {
        // Use getMessagesFromConversation to fetch latest, marking as read
        // Limit to a reasonable large number for a "reload" scenario
        const data = await getMessagesFromConversation(
            conversationId,
            limit, // Or maybe a different limit for reload?
            null, // Fetch latest
            true // Mark as read
        )

        // Add messages to IndexedDB (async, fire-and-forget is okay here)
        if (data?.messages?.length > 0) {
            putMessages(data.messages).catch((err) => {
                console.error('DB Error saving reloaded messages:', err)
            })
        }

        return data // { messages: Array, hasMoreMessages: boolean }
    } catch (error) {
        console.error('Failed to reload conversation messages via API:', error)
        throw error
    }
}

/**
 * Sync messages with backend
 * @param {string} lastSyncedAt - Last synced timestamp
 * @returns {Promise<Array>} - Messages which should be updated
 */
const syncMessagesWithBackend = async (lastSyncedAt) => {
    try {
        const response = await apiClient.post('/messages/sync', {
            lastSyncedAt,
        })
        return response.data
    } catch (error) {
        console.error('Failed to sync messages with backend:', error)
        throw new Error(
            'Failed to sync messages with backend: ' + error.message
        )
    }
}

export default {
    getMessagesFromConversation,
    getInitialConversationsWithLastMessages,
    getNewMessages,
    sendMessage,
    typing,
    stopTyping,
    markMessagesAsReceived,
    markMessagesAsRead,
    reloadConversationMessages,
    syncMessagesWithBackend,
    fetchTranslationsForMessages,
}
